import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Search } from 'lucide-react';
import { useConceptsStore } from '@/stores/concepts';
import { useKnowledgeStore } from '@/stores/knowledge';
import type { RawConceptData } from '@/services/api';

const RESOLUTION_STATUSES = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'canonical', label: 'Canonical' },
  { value: 'skipped', label: 'Skipped' },
];

function formatLabel(s: string): string {
  return s
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const STATUS_DOT: Record<string, string> = {
  resolved: 'bg-emerald-500',
  canonical: 'bg-brand-teal',
  pending: 'bg-amber-400',
  skipped: 'bg-slate-300',
};

export function ConceptList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialResearchId = searchParams.get('research_id') ?? '';

  const {
    concepts,
    selectedConcept,
    filters,
    loading,
    setFilters,
    selectConcept,
    fetchConcepts,
  } = useConceptsStore();

  const { entityTypes, fetchEntityTypes } = useKnowledgeStore();

  useEffect(() => {
    if (initialResearchId && !filters.researchId) {
      setFilters({ researchId: initialResearchId });
    } else {
      fetchConcepts();
    }
    fetchEntityTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = filters.search.trim()
    ? concepts.filter((c) =>
        c.name.toLowerCase().includes(filters.search.toLowerCase()),
      )
    : concepts;

  return (
    <div className="flex flex-col h-full bg-white border-r border-slate-200">
      <div className="p-3 space-y-2 border-b border-slate-100">
        <select
          value={filters.entityType}
          onChange={(e) => setFilters({ entityType: e.target.value })}
          className="w-full bg-slate-50 text-sm text-slate-700 rounded-lg px-2 py-1.5 border border-slate-200 focus:border-brand-teal/50 focus:outline-none focus:ring-1 focus:ring-brand-teal/30"
        >
          <option value="">All Entity Types</option>
          {entityTypes.map((t) => (
            <option key={t} value={t}>
              {formatLabel(t)}
            </option>
          ))}
        </select>
        <select
          value={filters.resolutionStatus}
          onChange={(e) => setFilters({ resolutionStatus: e.target.value })}
          className="w-full bg-slate-50 text-sm text-slate-700 rounded-lg px-2 py-1.5 border border-slate-200 focus:border-brand-teal/50 focus:outline-none focus:ring-1 focus:ring-brand-teal/30"
        >
          {RESOLUTION_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Client UID (optional)"
          value={filters.clientUid}
          onChange={(e) => setFilters({ clientUid: e.target.value })}
          onBlur={() => fetchConcepts()}
          className="w-full bg-slate-50 text-sm text-slate-700 rounded-lg px-2 py-1.5 border border-slate-200 focus:border-brand-teal/50 focus:outline-none focus:ring-1 focus:ring-brand-teal/30 placeholder-slate-400"
        />
        {filters.researchId && (
          <div className="flex items-center justify-between text-xs bg-brand-teal/5 border border-brand-teal/20 px-2 py-1 rounded">
            <span className="text-navy-900 truncate">
              research_id: <code className="text-brand-teal">{filters.researchId.slice(0, 8)}</code>
            </span>
            <button
              onClick={() => {
                setFilters({ researchId: '' });
                navigate('/knowledge/estate/concepts');
              }}
              className="text-slate-500 hover:text-slate-700"
            >
              Clear
            </button>
          </div>
        )}
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
          <input
            type="text"
            placeholder="Filter by name..."
            value={filters.search}
            onChange={(e) => setFilters({ search: e.target.value })}
            className="w-full bg-slate-50 text-sm text-slate-700 rounded-lg pl-8 pr-2 py-1.5 border border-slate-200 focus:border-brand-teal/50 focus:outline-none focus:ring-1 focus:ring-brand-teal/30 placeholder-slate-400"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading && filtered.length === 0 ? (
          <div className="p-4 text-sm text-slate-400 text-center">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-4 text-sm text-slate-400 text-center">
            No concepts found.
          </div>
        ) : (
          filtered.map((c) => (
            <ConceptRow
              key={c.id}
              concept={c}
              selected={selectedConcept?.id === c.id}
              onClick={() => {
                selectConcept(c);
                navigate(`/knowledge/estate/concepts/${c.id}`);
              }}
            />
          ))
        )}
      </div>
    </div>
  );
}

function ConceptRow({
  concept,
  selected,
  onClick,
}: {
  concept: RawConceptData;
  selected: boolean;
  onClick: () => void;
}) {
  const status = concept.resolution_status ?? 'pending';
  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 transition-colors',
        selected && 'bg-slate-50 border-l-2 border-l-brand-teal',
      )}
    >
      <div className="flex items-center gap-2">
        <span
          className={clsx(
            'w-1.5 h-1.5 rounded-full flex-shrink-0',
            STATUS_DOT[status] ?? 'bg-slate-300',
          )}
          title={status}
        />
        <span className="text-sm font-medium text-slate-800 truncate">
          {concept.name || concept.id.slice(0, 12)}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 pl-3.5">
        <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
          {concept.entity_type}
        </span>
        {concept.canonical_entity_id && (
          <span className="text-emerald-600 text-[10px] font-medium">resolved</span>
        )}
        <span className="ml-auto">v{concept.version}</span>
      </div>
    </button>
  );
}
